import { create } from 'zustand';
import { onPermissionChange, queryPermission, requestPosition } from '../services/geolocation';
import { Logger } from '../services/logger';

const log = new Logger('LocationGate');

export type LocationStatus = 'checking' | 'serviceDisabled' | 'denied' | 'deniedForever' | 'granted';
export const isUsable = (s: LocationStatus): boolean => s === 'granted';

interface LocationState {
  status: LocationStatus;
  /** Re-evaluate without prompting (used when the app becomes visible again). */
  refresh: () => Promise<void>;
  /** Prompt for permission. */
  request: () => Promise<void>;
  /** Watch the browser permission and refresh on change. Returns an unsubscribe. */
  watch: () => () => void;
}

async function check(): Promise<LocationStatus> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return 'serviceDisabled';
  switch (await queryPermission()) {
    case 'granted':
      return 'granted';
    case 'denied':
      // Browsers do not re-prompt after a denial; the user must change it in site settings.
      return 'deniedForever';
    default:
      return 'denied'; // "prompt", or a browser that cannot say
  }
}

export const useLocation = create<LocationState>((set, get) => ({
  status: 'checking',
  refresh: async () => set({ status: await check() }),
  request: async () => {
    set({ status: 'checking' });
    const result = await requestPosition();
    log.info('permission request result', { result });
    if (result === 'granted') set({ status: 'granted' });
    else if (result === 'unavailable') set({ status: 'serviceDisabled' });
    else set({ status: 'deniedForever' });
  },
  watch: () => {
    void get().refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void get().refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    const off = onPermissionChange(() => void get().refresh());
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      off();
    };
  },
}));
